<template>
  <!-- Main Content -->
  <div>
    <!-- Breadcrumb -->
    <div class="page-header">
      <div class="page-block">
        <div class="row align-items-center">
          <div class="col-md-12">
            <div class="page-header-title">
              <h5>{{ $t('rfq.title') || 'Request for Quotations' }}</h5>
            </div>
            <ul class="breadcrumb">
              <li class="breadcrumb-item">
                <router-link to="/">{{ $t('common.navigation.home') }}</router-link>
              </li>
              <li class="breadcrumb-item active">{{ $t('rfq.listTitle') || 'List' }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <!-- Main Content -->
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <ul class="nav nav-tabs invoice-tab border-bottom-0" id="myTab" role="tablist">
                <li class="nav-item" role="presentation">
                  <button
                    class="nav-link"
                    :class="{ 'active': activeTab === 'all' }"
                    @click="setActiveTab('all')"
                    type="button"
                    role="tab"
                  >
                    <span class="d-flex align-items-center gap-2">
                      {{ $t('rfq.tabs.all') || 'All' }} 
                      <span class="avtar rounded-circle bg-light-primary">{{ totalStatusCount }}</span>
                    </span>
                  </button>
                </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ 'active': activeTab === 'waiting' }"
                  @click="setActiveTab('waiting')"
                  type="button"
                  role="tab"
                >
                  <span class="d-flex align-items-center gap-2">
                    {{ $t('rfq.tabs.waiting') || 'Waiting For Quotation' }} 
                    <span class="avtar rounded-circle bg-light-warning">{{ statusCounts.waiting || 0 }}</span>
                  </span>
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ 'active': activeTab === 'submitted' }"
                  @click="setActiveTab('submitted')"
                  type="button"
                  role="tab"
                >
                  <span class="d-flex align-items-center gap-2">
                    {{ $t('rfq.tabs.submitted') || 'Quotation Submitted' }} 
                    <span class="avtar rounded-circle bg-light-success">{{ statusCounts.submitted + statusCounts.readyToSend || 0 }}</span>
                  </span>
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ 'active': activeTab === 'revision' }"
                  @click="setActiveTab('revision')"
                  type="button"
                  role="tab"
                >
                  <span class="d-flex align-items-center gap-2">
                    {{ $t('rfq.tabs.revision') || 'Revision Requested' }} 
                    <span class="avtar rounded-circle bg-light-purple">{{ statusCounts.revision || 0 }}</span>
                  </span>
                </button>
              </li>
              <li class="nav-item" role="presentation">
                <button
                  class="nav-link"
                  :class="{ 'active': activeTab === 'declined' }"
                  @click="setActiveTab('declined')"
                  type="button"
                  role="tab"
                >
                  <span class="d-flex align-items-center gap-2">
                    {{ $t('rfq.tabs.declined') || 'Declined' }} 
                    <span class="avtar rounded-circle bg-light-danger">{{ statusCounts.declined || 0 }}</span>
                  </span>
                </button>
              </li>
              </ul>
            </div>
            
            <div class="tab-content" id="myTabContent">
              <!-- All Requests Tab -->
              <div 
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === 'all' }"
                role="tabpanel"
                tabindex="0"
              >
                <DataGrid
                  :data="allRequests"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="itemsPerPage"
                  v-model:searchText="searchText"
                  v-model:sortField="sortField"
                  v-model:sortDirection="sortDirection"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="'No requests found.'"
                  :search-placeholder="$t('common.search.inRequests')"
                  :total-items="totalItems"
                  :storage-key="'rfqList_all_columnWidths'"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @sort="handleSort"
                  @refresh="fetchQuotations"
                  @view="viewRequest"
                  @edit="editRequest"
                  @delete="deleteRequest"
                  :renderActions="(item) => item.status === 'Declined' ? ['view'] : ['view', 'edit', 'delete']"
                />
              </div>

              <!-- Waiting Requests Tab -->
              <div 
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === 'waiting' }"
                role="tabpanel" 
                tabindex="0"
              >
                <DataGrid
                  :data="waitingRequests"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="itemsPerPage"
                  v-model:searchText="searchText"
                  v-model:sortField="sortField"
                  v-model:sortDirection="sortDirection"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="'No waiting quotation requests found.'"
                  :search-placeholder="$t('common.search.inRequests')"
                  :total-items="totalItems"
                  :storage-key="'rfqList_waiting_columnWidths'"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @sort="handleSort"
                  @refresh="fetchQuotations"
                  @view="viewRequest"
                  @edit="editRequest"
                  @delete="deleteRequest"
                  :renderActions="(item) => item.status === 'Declined' ? ['view'] : ['view', 'edit', 'delete']"
                />
              </div>

              <!-- Submitted Requests Tab -->
              <div 
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === 'submitted' }"
                role="tabpanel" 
                tabindex="0"
              >
                <DataGrid
                  :data="submittedRequests"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="itemsPerPage"
                  v-model:searchText="searchText"
                  v-model:sortField="sortField"
                  v-model:sortDirection="sortDirection"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="'No submitted quotation requests found.'"
                  :search-placeholder="$t('common.search.inRequests')"
                  :total-items="totalItems"
                  :storage-key="'rfqList_submitted_columnWidths'"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @sort="handleSort"
                  @refresh="fetchQuotations"
                  @view="viewRequest"
                  @edit="editRequest"
                  @delete="deleteRequest"
                  :renderActions="(item) => ['view']"
                />
              </div>

              <!-- Revision Requests Tab -->
              <div 
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === 'revision' }"
                role="tabpanel" 
                tabindex="0"
              >
                <DataGrid
                  :data="revisionRequests"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="itemsPerPage"
                  v-model:searchText="searchText"
                  v-model:sortField="sortField"
                  v-model:sortDirection="sortDirection"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="'No revision requests found.'"
                  :search-placeholder="$t('common.search.inRequests')"
                  :total-items="totalItems"
                  :storage-key="'rfqList_revision_columnWidths'"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @sort="handleSort"
                  @refresh="fetchQuotations"
                  @view="viewRequest"
                  @edit="editRequest"
                  @delete="deleteRequest"
                  :renderActions="(item) => item.status === 'Declined' ? ['view'] : ['view', 'edit', 'delete']"
                />
              </div>
              
              <!-- Declined Requests Tab -->
              <div 
                class="tab-pane fade" 
                :class="{ 'show active': activeTab === 'declined' }"
                role="tabpanel" 
                tabindex="0"
              >
                <DataGrid
                  :data="declinedRequests"
                  :columns="columns"
                  v-model:currentPage="currentPage"
                  v-model:pageSize="itemsPerPage"
                  v-model:searchText="searchText"
                  v-model:sortField="sortField"
                  v-model:sortDirection="sortDirection"
                  :loading="isLoading"
                  :error="error"
                  :empty-state-message="'No declined quotations found.'"
                  :search-placeholder="$t('common.search.inRequests')"
                  :total-items="totalItems"
                  :storage-key="'rfqList_declined_columnWidths'"
                  :show-refresh="true"
                  @search="handleSearch"
                  @pageSizeChange="handlePageSizeChange"
                  @pageChange="handlePageChange"
                  @sort="handleSort"
                  @refresh="fetchQuotations"
                  @view="viewRequest"
                  @edit="editRequest"
                  @delete="deleteRequest"
                  :renderActions="['view']"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>

  
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import quotationService from '@/services/quotationService';
import TrackingStatus from '../constants/trackingStatus';
import DataGrid from '@/components/DataGrid.vue';
import { useDateFormatter } from '@/composables/useDateFormatter'

export default {
  name: 'RfqList',
  components: {
    DataGrid
  },
  setup() {
    const router = useRouter();
    const { formatDate } = useDateFormatter();
    const activeTab = ref('all');
    const currentPage = ref(1);
    const itemsPerPage = ref(10);
    const requests = ref([]);
    const isLoading = ref(false);
    const error = ref(null);
    const totalItems = ref(0);
    const searchText = ref('');
    const sortField = ref('');
    const sortDirection = ref('asc');
    const statusCounts = ref({
      waiting: 0,
      submitted: 0,
      revision: 0,
      declined: 0,
      processing: 0,
      processedWithError: 0,
      readyToSend: 0
    });

    // Define columns for the DataGrid
    const columns = [
      { key: 'buyer', label: 'Buyer', width: '12%', sortable: true, sortKey: 'buyerTenantName' },
      { key: 'vessel', label: 'Vessel', width: '12%', sortable: true, sortKey: 'vesselName' },
      { key: 'rfqNo', label: 'RFQ No', width: '8%', sortable: true, sortKey: 'requestForQuotationCode' },
      { key: 'deliveryPort', label: 'Delivery Port', width: '8%', sortable: true, sortKey: 'deliveryPortDisplay' },
      { key: 'subject', label: 'Subject', width: '10%', type: 'truncate', sortable: true, sortKey: 'subject' },
      { key: 'description', label: 'Description', width: '14%', type: 'truncate', sortable: true, sortKey: 'description' },
      { 
        key: 'hasAttachment', 
        label: 'Attachment', 
        width: '5%', 
        type: 'icon',
        iconClass: 'ti ti-paperclip f-18 text-primary',
        sortable: false
      },
      { key: 'itemCount', label: 'Items', width: '5%', sortable: true, sortKey: 'numberOfItems' },
      { key: 'dueDate', label: 'Due Date', width: '7%', sortable: true, sortKey: 'dueDate' },
      { 
        key: 'status', 
        label: 'Status', 
        width: '8%', 
        type: 'badge',
        sortable: true,
        sortKey: 'trackingStatus',
        badgeMap: {
          'Waiting For Quotation': 'badge bg-light-warning',
          'Quotation Submitted': 'badge bg-light-success',
          'Revision Requested': 'badge bg-light-purple',
          'Declined': 'badge bg-light-danger',
          'Ready To Send': 'badge bg-light-success'
        }
      }
    ];

    const allRequests = computed(() => requests.value);
    
    const waitingRequests = computed(() => 
      requests.value.filter(req => req.status === 'Waiting For Quotation')
    );
    
    const submittedRequests = computed(() => 
      requests.value.filter(req => 
        req.status === 'Quotation Submitted' || req.status === 'Ready To Send'
      )
    );
    
    const revisionRequests = computed(() => 
      requests.value.filter(req => req.status === 'Revision Requested')
    );
    
    const declinedRequests = computed(() => 
      requests.value.filter(req => req.status === 'Declined')
    );

    // Compute the total of all status counts
    const totalStatusCount = computed(() => {
      // Include all status counts in the total
      let total = 0;
      for (const key in statusCounts.value) {
        if (Object.prototype.hasOwnProperty.call(statusCounts.value, key)) {
          total += statusCounts.value[key];
        }
      }
      return total;
    });

    // Reset page when changing tabs
    const setActiveTab = (tab) => {
      activeTab.value = tab;
      currentPage.value = 1;
      // fetchQuotations() is called by watch(activeTab)
    };

    // Handle search
    const handleSearch = () => {
      currentPage.value = 1;
      fetchQuotations();
    };
    
    // Handle sort
    const handleSort = () => {
      currentPage.value = 1;
      fetchQuotations();
    };
    
    // Get the current tracking status based on active tab
    const getCurrentTrackingStatus = () => {
      switch (activeTab.value) {
        case 'all': 
          return undefined; // Don't send parameter for All tab
        case 'waiting': 
          return [TrackingStatus.New]; // 1
        case 'submitted': 
          return [TrackingStatus.ProcessedSuccessfully, TrackingStatus.ReadyToSendToNozzle]; // 5, 6
        case 'revision': 
          return [TrackingStatus.ReNew]; // 2
        case 'declined': 
          return [TrackingStatus.Declined, TrackingStatus.DeclinedSuccessfully]; // 10, 11
        default: 
          return undefined;
      }
    };

    // Map API tracking status to display status
    const mapTrackingStatus = (trackingStatus) => {
      switch (trackingStatus) {
        case TrackingStatus.New:
          return 'Waiting For Quotation';
        case TrackingStatus.ProcessedSuccessfully:
          return 'Quotation Submitted';
        case TrackingStatus.ReNew:
          return 'Revision Requested';
        case TrackingStatus.ReadyToSendToNozzle:
          return 'Quotation Submitted';
        case TrackingStatus.Declined:
          return 'Declined';
        case TrackingStatus.DeclinedSuccessfully:
          return 'Declined';
        default:
          return 'Unknown Status';
      }
    };

    // Fetch quotations from API
    const fetchQuotations = async () => {
      isLoading.value = true;
      error.value = null;
      
      try {
        const searchParams = {
          page: currentPage.value,
          pageSize: itemsPerPage.value,
          searchText: searchText.value
        };
        
        // Add sorting if sortField is set
        if (sortField.value) {
          searchParams.sorting = `${sortField.value} ${sortDirection.value}`;
        }
        
        // Only add trackingStatus if it's not undefined
        const status = getCurrentTrackingStatus();
        if (status !== undefined) {
          searchParams.trackingStatus = status;
        }
        
        
        const response = await quotationService.searchQuotations(searchParams);
        
        if (response) {
          
          // Transform API data to match our component's expected format
          requests.value = response.items.map(item => ({
            id: item.id,
            buyer: item.buyerTenantName || '',
            vessel: item.vesselName || '',
            rfqNo: item.requestForQuotationCode || '',
            deliveryPort: item.deliveryPortDisplay || '',
            subject: item.subject || '',
            description: item.description || '',
            hasAttachment: item.hasAttachment || false,
            itemCount: item.numberOfItems || 0,
            dueDate: formatDate(item.dueDate),
            status: mapTrackingStatus(item.trackingStatus),
            trackingStatus: item.trackingStatus
          }));
          
          // Update total count for pagination
          totalItems.value = response.rowCount || 0;
          
          // Update status counts from API
          if (response.statusCounts) {
            // Use direct mapping from API response
            statusCounts.value = {
              waiting: response.statusCounts.New || 0,
              submitted: response.statusCounts.ProcessedSuccessfully || 0,
              revision: response.statusCounts.ReNew || 0,
              readyToSend: response.statusCounts.ReadyToSendToNozzle || 0,
              declined: (response.statusCounts.Declined || 0) + (response.statusCounts.DeclinedSuccessfully || 0),
              // Include other possible status values
              processing: response.statusCounts.Processing || 0,
              processedWithError: response.statusCounts.ProcessedWithError || 0
            };
          }
        }
      } catch (err) {
        console.error('Error fetching quotations:', err);
        error.value = 'Failed to load quotations. Please try again.';
      } finally {
        isLoading.value = false;
      }
    };

    const viewRequest = (item) => {
      router.push(`/rfq/${item.id}`);
    };

    const editRequest = (item) => {
      // For Quotation Submitted, Ready To Send, or Declined, don't add edit=true
      if (item.status === 'Quotation Submitted' || item.status === 'Declined' || item.status === 'Ready To Send') {
        router.push(`/rfq/${item.id}`);
      } else {
        router.push(`/rfq/${item.id}?edit=true`);
      }
    };

    const deleteRequest = async (item) => {
      const confirmed = confirm('Are you sure you want to delete this request?');
      if (confirmed) {
        try {
          await quotationService.deleteQuotation(item.id);
          // Refresh the list
          fetchQuotations();
        } catch (err) {
          console.error('Error deleting quotation:', err);
          alert('Failed to delete quotation. Please try again.');
        }
      }
    };

    // Handle page size change
    const handlePageSizeChange = () => {
      currentPage.value = 1;
      fetchQuotations();
    };

    // Watch for changes in activeTab
    watch(activeTab, (newTab, oldTab) => {
      fetchQuotations();
    });

    // Watch for changes in itemsPerPage
    watch(itemsPerPage, () => {
      currentPage.value = 1;
      fetchQuotations();
    });

    // Handle page change
    const handlePageChange = () => {
      fetchQuotations();
    };

    onMounted(() => {
      // Fetch quotations when component is mounted
      fetchQuotations();
    });

    return {
      activeTab,
      setActiveTab,
      columns,
      currentPage,
      itemsPerPage,
      searchText,
      handleSearch,
      handlePageSizeChange,
      handlePageChange,
      allRequests,
      waitingRequests,
      submittedRequests,
      revisionRequests,
      declinedRequests,
      viewRequest,
      editRequest,
      deleteRequest,
      isLoading,
      error,
      totalItems,
      statusCounts,
      totalStatusCount,
      formatDate,
      fetchQuotations,
      sortField,
      sortDirection,
      handleSort
    };
  }
};
</script>

<style scoped>
.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>

<style>
.bg-light-purple {
  background-color: rgba(138, 43, 226, 0.15) !important;
  color: #8a2be2 !important;
}

.badge.bg-light-purple {
  background-color: #8a2be2 !important;
  color: #ffffff !important;
}

.badge.bg-light-warning {
  background-color: #ffc107 !important;
  color: #212529 !important;
}
.badge.bg-light-success {
  background-color: #198754 !important;
  color: #ffffff !important;
}
.badge.bg-light-danger {
  background-color: #dc3545 !important;
  color: #ffffff !important;
}
</style>